import type {
  PartnerCostAdjustConfig,
  PartnerLimitV2,
  PartnerLimitConstraintV2,
  PartnerLimitCreateV2,
  PartnerLimitUpdateV2,
} from '@shipium-corp/shipium-ts-types';
import type { V2LimitUpdatePayload } from '~/store/services/LimitService/types';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';

export const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

export const dayOfWeekAbbr: Record<string, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};
interface LimitFormData {
  carrier?: string;
  carrierServiceMethodIds?: string[];
  originId: string;
  tenantId?: string;
  effectiveDate: Date;
  expirationDate?: Date;
  name?: string;
  limitType: PartnerLimitV2['limitType'];
  limits: LimitChipFormData[];
}
interface LimitChipFormData {
  maximum: string;
  minimum: string;
  discount: number;
  discountType: string;
  daysOfWeek: string[];
}

export type LimitFieldValue = string | number | null | undefined;

// Fields seeded from an existing limit hold the API's numeric thresholdValue; typed input is a string.
export const toLimitText = (value: LimitFieldValue) =>
  value === null || value === undefined ? '' : String(value).trim();

export const initialRowDayStatusFromFormData = (formData: LimitChipFormData[]) => {
  const result: Record<string, number> = {};
  for (let idx = 0; idx < formData.length; idx++) {
    for (const dow of formData[idx].daysOfWeek) {
      result[dow] = idx;
    }
  }
  return result;
};

export const buildFormDataFromLimit = (limit: PartnerLimitV2) => {
  const groupedByDow: Record<string, any> = {};
  for (const val of limit.constraints) {
    const existing = groupedByDow[val.dayOfWeek] ?? {};
    if (val.constraintType === 'minimum') {
      groupedByDow[val.dayOfWeek] = {
        minimum: val.thresholdValue,
        discount:
          val.labelCostAdjustment?.type === 'percentage'
            ? (val.labelCostAdjustment?.value ?? 0) * 100
            : val.labelCostAdjustment?.value,
        discountType: val.labelCostAdjustment?.type,
        ...existing,
      };
    } else {
      groupedByDow[val.dayOfWeek] = {
        maximum: val.thresholdValue,
        ...existing,
      };
    }
  }
  const groupByValues = Object.entries(groupedByDow).reduce((prev: Record<string, any>, e) => {
    const [key, val] = e;
    // if max and min are the same we we group it together in the form(for these days of the week this is max in min)
    const hash = `${val.minimum}_${val.maximum}`;
    if (!prev[hash]) {
      prev[hash] = { ...val, daysOfWeek: [key] };
    } else {
      prev[hash] = { ...val, daysOfWeek: [...prev[hash].daysOfWeek, key] };
    }
    return prev;
  }, {});
  const formData = Object.values(groupByValues);
  return formData;
};

const buildConstraintsFromFormData = (formData: LimitFormData) => {
  const maximums = formData.limits
    .filter((lim) => toLimitText(lim.maximum) !== '')
    .reduce((limits: PartnerLimitConstraintV2[], data) => {
      return [
        ...limits,
        ...data.daysOfWeek.map(
          (dow) =>
            ({
              constraintType: 'maximum',
              dayOfWeek: dow,
              thresholdType: 'shipment_count',
              thresholdValue: Number(data.maximum),
            }) as PartnerLimitConstraintV2,
        ),
      ];
    }, []);
  const minimums = formData.limits
    .filter((lim) => toLimitText(lim.minimum) !== '')
    .reduce((limits: PartnerLimitConstraintV2[], data) => {
      return [
        ...limits,
        ...data.daysOfWeek.map(
          (dow) =>
            ({
              constraintType: 'minimum',
              dayOfWeek: dow,
              thresholdType: 'shipment_count',
              thresholdValue: Number(data.minimum),
              labelCostAdjustment: {
                type: data.discountType,
                value: data.discountType === 'percentage' ? Number(data.discount) / 100 : Number(data.discount),
              },
            }) as PartnerLimitConstraintV2,
        ),
      ];
    }, []);
  return [...minimums, ...maximums];
};

export const rowDataToUpdateLimit = (row: PartnerLimitV2, isActive: boolean): V2LimitUpdatePayload => {
  const { archive, constraints, effectiveDate, expirationDate, localCutoffTime, localZoneId, name, limitId } = row;

  return {
    active: isActive,
    archive,
    constraints,
    effectiveDate,
    expirationDate,
    localCutoffTime,
    localZoneId,
    name,
    limitId,
  };
};

export const formDataToLimit = (
  formData: LimitFormData,
  edit: boolean,
): PartnerLimitCreateV2 | PartnerLimitUpdateV2 => {
  const limitFormatDate = 'yyyy-MM-dd';
  if (edit) {
    const update: Partial<PartnerLimitUpdateV2> = {};
    update.effectiveDate = format(formData.effectiveDate, limitFormatDate);
    update.expirationDate = formData.expirationDate ? format(formData.expirationDate, limitFormatDate) : undefined;
    update.constraints = buildConstraintsFromFormData(formData);
    return update as PartnerLimitUpdateV2;
  } else {
    const create: Partial<PartnerLimitCreateV2> = {};
    create.name = formData.name;
    create.effectiveDate = format(formData.effectiveDate, limitFormatDate);
    create.expirationDate = formData.expirationDate ? format(formData.expirationDate, limitFormatDate) : undefined;
    create.carrier = formData.carrier;
    create.tenantId = formData.tenantId;
    create.carrierServiceMethodIds = formData.carrierServiceMethodIds ?? [];
    create.originId = formData.originId;
    create.limitType = formData.limitType;
    create.constraints = buildConstraintsFromFormData(formData);
    return create as PartnerLimitCreateV2;
  }
};
export const limitToFormData = (limit: PartnerLimitV2): LimitFormData => {
  const { effectiveDate, expirationDate, originId, carrier, carrierServiceMethodIds, shipiumTenantId, constraints } =
    limit;
  const limitsByDay = constraints.reduce((prev: any, val) => {
    if (!prev[val.dayOfWeek]) {
      prev[val.dayOfWeek] =
        val.constraintType === 'maximum'
          ? { maximum: val.thresholdValue }
          : {
              minimum: val.thresholdValue,
              discount:
                val.labelCostAdjustment?.type === 'percentage'
                  ? 100 * val.labelCostAdjustment?.value
                  : val.labelCostAdjustment?.value,
              discountType: val.labelCostAdjustment?.type,
            };
    } else {
      const newInfo =
        val.constraintType === 'maximum'
          ? { maximum: val.thresholdValue }
          : {
              minimum: val.thresholdValue,
              discount:
                val.labelCostAdjustment?.type === 'percentage'
                  ? 100 * val.labelCostAdjustment?.value
                  : val.labelCostAdjustment?.value,
              discountType: val.labelCostAdjustment?.type,
            };
      prev[val.dayOfWeek] = { ...prev[val.dayOfWeek], ...newInfo };
    }
    return prev;
  }, {});
  return {
    ...limitsByDay,
    carrier,
    effectiveDate: effectiveDate ? parse(effectiveDate, 'yyyy-MM-dd', new Date()) : effectiveDate,
    expirationDate: expirationDate ? parse(expirationDate, 'yyyy-MM-dd', new Date()) : expirationDate,
    originId,
    tenantId: shipiumTenantId,
    carrierServiceMethodIds,
  };
};

const LIMIT_TYPE_LABELS: Record<NonNullable<PartnerLimitV2['limitType']>, string> = {
  label: 'Labels Only',
  carrier_selection: 'Carrier Selection and Labels',
};

export const limitTypeLabel = (limitType: PartnerLimitV2['limitType']): string =>
  limitType ? LIMIT_TYPE_LABELS[limitType] : '';

export const constraintValues = (limit: Pick<PartnerLimitV2, 'constraints'>, type: 'maximum' | 'minimum'): number[] =>
  (limit.constraints ?? []).filter((c) => c.constraintType === type).map((c) => c.thresholdValue);

// -Infinity for rows with no maximum so they sort below any concrete value.
export const maxCountSortKey = (limit: Pick<PartnerLimitV2, 'constraints'>): number => {
  const values = constraintValues(limit, 'maximum');
  return values.length === 0 ? -Infinity : Math.max(...values);
};

export const formatMaxMinLine = (label: string, values: number[]): string | undefined => {
  if (values.length === 0) return undefined;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return `${label}: ${lo === hi ? hi : `${lo}–${hi}`}`;
};

/* ISO-8601 Duration
https://www.digi.com/resources/documentation/digidocs/90001488-13/reference/r_iso_8601_duration_format.htm
Cost Adjust Config API uses this to calculate rolling average percentage of total shipments
to compare with minimum percentage threshold for the cost adjustment config (dynamic limit)
Period: 7 Days
*/
export const DYNAMIC_LIMIT_EVALUATION_PERIOD: PartnerCostAdjustConfig['evaluationPeriod'] = 'P2D';
